import type { ProductAttributeT } from '../type/product/productAttributes';
import type { ProductToCompareT } from '../type/product/productComparison';

import isEqual from 'lodash/isEqual';

export type AttributeValue = ProductAttributeT['value'];

/**
 * Returns the value for a given attribute from a product, or null if not present.
 */
export function getAttributeValueForProduct(
  product: ProductToCompareT,
  attribute: ProductAttributeT
): AttributeValue {
  for (const group of product.groupedAttributes) {
    const found = group.attributes.find(
      (attr) => attr.name === attribute.name && attr.attributeGroupId === attribute.attributeGroupId
    );
    if (found) return found.value;
  }

  return null;
}

/**
 * Returns an array of values for the given attribute across all products.
 */
export function getAttributeValuesForProducts(
  products: readonly ProductToCompareT[],
  attribute: ProductAttributeT
): AttributeValue[] {
  return products.map((product) => getAttributeValueForProduct(product, attribute));
}

/**
 * Returns true if not all values in the array are equal (deep for arrays/objects/primitives).
 * Uses lodash's isEqual for robust deep equality comparison.
 */
export function hasAttributeRowDiffs(values: AttributeValue[]): boolean {
  if (values.length <= 1) return false;

  return values.some((v) => !isEqual(v, values[0]));
}
